import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Calendar } from "lucide-react";
import Markdown from "@/components/Markdown";

export function NewsListPage() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterCat, setFilterCat] = useState("all");
  useEffect(() => {
    api.get("/news?published_only=true").then(x => setItems(x.data)).catch(() => {});
    api.get("/news-categories").then(x => setCategories(x.data || [])).catch(() => {});
  }, []);
  const visible = filterCat === "all" ? items : items.filter(n => n.category === filterCat);
  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-14" data-testid="news-list-page">
      <div className="h-1 w-12 bg-[#00E5FF] mb-4" />
      <h1 className="font-display font-black italic uppercase tracking-normal text-5xl sm:text-7xl leading-none">News</h1>
      <p className="text-neutral-400 mt-3 max-w-2xl">Aktuelles rund um die Liga, Teams und Fahrer.</p>
      {categories.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mt-8" data-testid="news-filter-bar">
          <button
            type="button"
            onClick={() => setFilterCat("all")}
            className={`text-xs uppercase tracking-widest px-3 py-1.5 border ${filterCat === "all" ? "border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]" : "border-white/15 text-neutral-400 hover:border-white/40"}`}
            data-testid="news-filter-all"
          >Alle ({items.length})</button>
          {categories.map(c => {
            const count = items.filter(n => n.category === c.name).length;
            if (count === 0) return null;
            const active = filterCat === c.name;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setFilterCat(c.name)}
                className="text-xs uppercase tracking-widest px-3 py-1.5 border transition-colors"
                style={active ? { borderColor: c.color, background: `${c.color}1A`, color: c.color } : { borderColor: "rgba(255,255,255,0.15)", color: "#a3a3a3" }}
                data-testid={`news-filter-${c.id}`}
              >{c.name} ({count})</button>
            );
          })}
        </div>
      )}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
        {visible.map((n) => {
          const catColor = categories.find(c => c.name === n.category)?.color || "#00E5FF";
          return (
          <Link key={n.id} to={`/news/${n.slug}`} className="card-surface block group overflow-hidden" data-testid={`news-card-${n.id}`}>
            <div className="relative h-44 overflow-hidden">
              {n.hero_image ? <img src={n.hero_image} alt={n.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" /> :
                <div className="w-full h-full bg-gradient-to-br from-[#1F1F1F] to-[#0B0F19]" />}
            </div>
            <div className="p-5">
              <div className="eyebrow" style={{ color: catColor }}>{n.category}</div>
              <div className="font-display font-black italic uppercase text-xl mt-2 leading-tight transition-colors" style={{ color: undefined }}>{n.title}</div>
              <p className="text-sm text-neutral-400 mt-2 line-clamp-3">{n.summary}</p>
              <div className="mt-4 text-xs text-neutral-500 flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{new Date(n.created_at).toLocaleDateString("de-DE")}</div>
            </div>
          </Link>
          );
        })}
        {visible.length === 0 && <div className="text-neutral-500">Keine Artikel in dieser Kategorie.</div>}
      </div>
    </div>
  );
}

export function NewsDetailPage() {
  const { slug } = useParams();
  const [n, setN] = useState(null);
  const [categories, setCategories] = useState([]);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get(`/news/${slug}`).then(x => setN(x.data)).catch(e => setErr(e?.response?.data?.detail || "Nicht gefunden"));
    api.get("/news-categories").then(x => setCategories(x.data || [])).catch(() => {});
  }, [slug]);
  if (err) return <div className="max-w-3xl mx-auto px-6 py-20 text-neutral-400" data-testid="news-not-found">{String(err)}</div>;
  if (!n) return <div className="max-w-3xl mx-auto px-6 py-20 text-neutral-400">Lade...</div>;
  const catColor = categories.find(c => c.name === n.category)?.color || "#00E5FF";
  return (
    <article className="pb-20" data-testid="news-detail-page">
      <div className="relative h-[40vh] min-h-[300px] overflow-hidden">
        {n.hero_image && <img src={n.hero_image} alt={n.title} className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/30 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 max-w-4xl mx-auto px-6 md:px-10 pb-8">
          <div className="h-1 w-16 mb-4" style={{ background: catColor }} />
          <div className="eyebrow" style={{ color: catColor }}>{n.category}</div>
          <h1 className="font-display font-black italic uppercase tracking-normal text-4xl sm:text-5xl leading-tight mt-2"
            style={{ textShadow: "0 4px 24px rgba(0,0,0,0.8)" }}>{n.title}</h1>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-6 md:px-10 mt-10">
        <p className="text-2xl text-white font-medium leading-snug mb-6">{n.summary}</p>
        <Markdown>{n.body}</Markdown>
      </div>
    </article>
  );
}
